<template>
  <div class="base-basic meeting-confirmation">
    <!-- 自定义头部 -->
    <van-nav-bar class="page-title" title="会议" />

    <!-- 内容区域 -->
    <div class="body-content">
      <!-- 使用独立的会议列表组件 -->
      <MeetingList ref="meetingListRef" :show-tabs="!isConfirmationPage" :show-search="true"
        :is-confirmation-page="isConfirmationPage" :meetings="meetings" :loading="loading" :loading-more="loadingMore"
        :finished="!hasMore" :initial-status="activeTab" @meeting-click="handleMeetingClick" @meeting-modify="handleModifyMeeting"
        @meeting-cancel="handleMeetingCancel" @confirm-attend="handleConfirmAttend" @cannot-attend="handleCannotAttend"
        @tab-change="handleTabChange" @load-more="handleLoadMore" @search="(keyword) => searchKeyword = keyword" />
    </div>

    <!-- 会议详情弹窗 -->
    <van-dialog v-model:show="showMeetingDetail" title="会议详情" width="90%" :show-cancel-button="false"
      confirm-button-text="关闭" @confirm="showMeetingDetail = false">
      <div class="dialog-content">
        <MeetingInfoCard :meeting-info="selectedMeeting" title="" :show-notice="false" />
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { NavBar, Dialog, showSuccessToast, showFailToast, showConfirmDialog } from 'vant';
import MeetingInfoCard from '../components/MeetingInfoCard.vue';
import MeetingList from '../components/MeetingList.vue';
import { useRouter, useRoute } from 'vue-router';
import axios from '@/api/axios';

export default {
  name: 'MeetingConfirmation',
  components: {
    'van-nav-bar': NavBar,
    MeetingInfoCard,
    MeetingList
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const meetingListRef = ref(null);
    const showMeetingDetail = ref(false);
    const selectedMeeting = ref(null);
    const meetings = ref([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const activeTab = ref(0);
    const page = ref(1);
    const total = ref(0);
    const hasMore = ref(true);
    const searchKeyword = ref('');
    // 组件挂载状态跟踪
    const isMounted = ref(true);

    // 判断当前是否是会议确认页面（通过路由路径区分）
    const isConfirmationPage = ref(route.path === '/confirm');
    // 判断当前是否是我的会议页面（通过路由路径区分）
    const isMyMeetingsPage = ref(route.path === '/my-meetings');

    // 根据当前选中的标签获取对应的status参数
    const statusMap = {
      0: '', // 全部
      1: 'confirmed', // 待开展/待审批
      2: 'pending', // 邀约中
      3: 'completed' // 已结束
    };

    // 获取会议列表
    const fetchMeetings = async (loadMore = false) => {
      try {
        // 只有初始加载才设置loading为true，加载更多时只设置loadingMore为true
        if (!loadMore) {
          loading.value = true;
        }

        // 获取approverId（会议ID），如果路由参数中没有提供，则从localStorage获取用户ID
        let approverId;
        if (route.params.approve_id) {
          // 如果路由参数中有approve_id，它是会议ID，用于搜索
          approverId = JSON.parse(localStorage.getItem('userInfo'))?.id || '1';
        } else {
          // 否则使用当前登录用户的ID作为审批者ID
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));
          if (!userInfo || !userInfo.id) {
            showFailToast('请先登录');
            router.push('/');
            return;
          }
          approverId = userInfo.id;
        }

        // 根据组件配置确定是否需要筛选状态
        let status;
        if (isConfirmationPage.value) {
          status = statusMap[0]; // 会议确认 展示是我审批的所有工单
        } else {
          status = statusMap[activeTab.value]; // 根据选中的标签获取状态
        }

        const currentUser = JSON.parse(localStorage.getItem('userInfo'));

        // 构建请求参数映射
        const paramsMap = {
          page: page.value,
          limit: 10,
          // 根据页面类型动态选择用户参数
          ...(isConfirmationPage.value ? { approverId } : { userId: currentUser.id }),
          // 可选参数
          status: status,
          meetingId: route.query.meetingId,
          expertName: searchKeyword.value
        };

        // 过滤掉值为空的参数
        const params = Object.fromEntries(
          Object.entries(paramsMap).filter(([key, value]) => value != null && value !== '')
        );

        // 调用接口获取会议列表数据
        const response = await axios.get('/api/meetings', { params });

        // 处理API响应
        if (isMounted.value) {
          if (response.data && response.data.success) {
            console.log('API Response:', response.data.meetings);
            // 检查API返回的结构
            let newMeetings = [];
            newMeetings = response.data.meetings;
            total.value = response.data.total || 0;

            // 智能合并数据：如果是重新加载且有现有数据，只在必要时更新
            if (loadMore) {
              // 加载更多时直接追加
              meetings.value.push(...newMeetings);
            } else {
              if (meetings.value.length === 0 || JSON.stringify(meetings.value) !== JSON.stringify(newMeetings)) {
                // 只在数据确实变化时才更新，避免不必要的重新渲染
                meetings.value = [...newMeetings];
              } else {
              }
            }

            // 判断是否还有更多数据
            hasMore.value = meetings.value.length < total.value;
          } else {
            showFailToast(response.data?.message || '获取会议列表失败');
            if (!loadMore) {
              meetings.value = [];
            }
          }
        }
      } catch (error) {
        showFailToast('获取会议列表失败，请检查网络连接后重试');

        if (!loadMore) {
          meetings.value = [];
        }
      } finally {
        loading.value = false;
      }
    };

    // 加载更多数据
    const handleLoadMore = async () => {
      if (!loading.value && hasMore.value && !loadingMore.value) {
        loadingMore.value = true;
        page.value++;
        await fetchMeetings(true);
        loadingMore.value = false;
      }
    };


    // 监听activeTab变化
    watch(activeTab, (newVal, oldVal) => {
      console.log('MeetingConfirmation - activeTab变化:', oldVal, '->', newVal);
    });

    // 处理标签切换
    const handleTabChange = (index) => {
      activeTab.value = index;
      page.value = 1;
      fetchMeetings();
    };

    // 处理会议点击
    const handleMeetingClick = (meeting) => {
      selectedMeeting.value = meeting;
      showMeetingDetail.value = true;
    };

    // 处理修改会议
    const handleModifyMeeting = (meeting) => {
      // 跳转到修改会议页面
      if (meeting && meeting.id) {
        router.push(`/meeting/${meeting.id}/edit`);
      } else {
        showFailToast('会议ID不存在，无法修改');
        // 尝试查找可能的ID字段
      }
    };

    // 处理取消会议
    const handleMeetingCancel = async (meeting) => {
      try {
        // 显示确认对话框
        await showConfirmDialog({
          title: '确认取消会议',
          message: '您确定要取消该会议吗？',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        });

        // 获取当前登录用户信息
        const currentUser = JSON.parse(localStorage.getItem('userInfo'));
        const operatorId = currentUser ? currentUser.id : '1'; // 默认值为'1'

        // 用户点击确认，调用取消会议接口
        const response = await axios.post(`/api/meetings/${meeting.id}/operate`, {
          status: 'cancelled',
          operatorId: operatorId,
          operatorType: 'creator'
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        // 处理API响应
        if (isMounted.value) {
          // 检查API响应结构 - 根据实际返回格式调整
          if (response.data && (response.data.code === 200 || response.data.data?.success)) {
            // 显示成功提示
            showSuccessToast('会议取消成功');

            // 使用局部更新代替重新加载整个列表，避免页面回闪
            const index = meetings.value.findIndex(m => m.id === meeting.id);
            if (index !== -1) {
              // 从列表中移除取消的会议
              meetings.value.splice(index, 1);
            }
          } else {
            // 显示API返回的错误信息或默认错误信息
            showFailToast('会议取消失败：' + (response.data?.message || response.data?.data?.message || '未知错误'));
          }
        }
      } catch (error) {
        // 如果是用户取消操作，不处理
        if (error === 'cancel') {
          return;
        }

        console.error('取消会议失败:', error);

        if (error.response) {
          // 服务器返回了错误状态码
          if (error.response.status === 401) {
            showFailToast('登录已过期，请重新登录');
            router.push('/');
          } else {
            showFailToast(`会议取消失败：${error.response.data.message}`);
          }
        } else if (error.request) {
          // 请求已发送但没有收到响应
          showFailToast('网络异常，请检查网络连接');
        } else {
          // 请求配置时发生错误
          showFailToast('会议取消失败，请稍后重试');
        }
      }
    };

    // 处理确认参会
    const handleConfirmAttend = async (meeting) => {
      try {
        // 获取当前登录用户信息
        const currentUser = JSON.parse(localStorage.getItem('userInfo'));
        const operatorId = currentUser ? currentUser.id : '1'; // 默认值为'1'

        // 从会议数据中获取专家信息
        const expert = meeting.experts && meeting.experts.length > 0 ? meeting.experts[0] : null;
        const expertId = expert ? expert.id : '';

        // 确认参会接口调用
        const response = await axios.post(`/api/meetings/${meeting.id}/operate`, {
          expertId: expertId,
          status: 'confirmed',
          operatorId: operatorId,
          operatorType: 'approver'
        });

        // 检查响应状态 - 兼容code和success两种格式
        if (isMounted.value) {
          if (response.data && (response.data.code === 200 || response.data.data?.success)) {
            showSuccessToast('已确认参会');
            // 使用局部更新代替重新加载整个列表，避免页面回闪
            const index = meetings.value.findIndex(m => m.id === meeting.id);
            if (index !== -1) {
              // 更新本地会议状态
              meetings.value[index] = { ...meetings.value[index], status: 'confirmed' };
            }
          } else {
            showFailToast('确认参会失败：' + (response.data?.message || response.data?.data?.message || '未知错误'));
          }
        }
      } catch (error) {
        console.error('确认参会失败:', error);
        if (error.response) {
          showFailToast(`确认参会失败：${error.response.data.message}`);
        } else if (error.request) {
          showFailToast('网络异常，请检查网络连接');
        } else {
          showFailToast('确认参会失败，请稍后重试');
        }
      }
    };

    // 处理无法参会
    const handleCannotAttend = async (meeting) => {
      try {
        // 获取当前登录用户信息
        const currentUser = JSON.parse(localStorage.getItem('userInfo'));
        const operatorId = currentUser ? currentUser.id : '1'; // 默认值为'1'

        // 从会议数据中获取专家信息
        const expert = meeting.experts && meeting.experts.length > 0 ? meeting.experts[0] : null;
        const expertId = expert ? expert.id : '';

        // 无法参会接口调用
        const response = await axios.post(`/api/meetings/${meeting.id}/operate`, {
          expertId: expertId,
          status: 'cannot_attend',
          operatorId: operatorId,
          operatorType: 'approver'
        });

        // 检查响应状态 - 兼容code和success两种格式
        if (isMounted.value) {
          if (response.data && (response.data.code === 200 || response.data.data?.success)) {
            showSuccessToast('已记录无法参会');
            // 使用局部更新代替重新加载整个列表，避免页面回闪
            const index = meetings.value.findIndex(m => m.id === meeting.id);
            if (index !== -1) {
              // 更新本地会议状态
              meetings.value[index] = { ...meetings.value[index], status: 'cannot_attend' };
            }
          } else {
            showFailToast('记录无法参会失败：' + (response.data?.message || response.data?.data?.message || '未知错误'));
          }
        }
      } catch (error) {
        showFailToast('操作失败');
        if (error.response) {
          showFailToast(`记录无法参会失败：${error.response.data.message}`);
        } else if (error.request) {
          showFailToast('网络异常，请检查网络连接');
        } else {
          showFailToast('记录无法参会失败，请稍后重试');
        }
      }
    };

    // 组件挂载时获取会议列表
    onMounted(() => {
      isMounted.value = true;
      // 移除自动调用fetchMeetings()，改为由路由监听负责第一次数据加载
      // 这样可以确保路由参数已经解析完毕，activeTab已经正确设置
    });

    // 组件卸载时清理资源
    onUnmounted(() => {
      isMounted.value = false;
    });

    // 监听搜索关键词变化，重置页码并重新获取数据
    watch(() => searchKeyword.value, (newKeyword) => {
      page.value = 1;
      fetchMeetings();
    });

        // 监听路由变化，更新状态值并重置数据
    watch([() => route.path, () => route.params, () => route.query], () => {
      console.log('MeetingConfirmation路由监听 - 触发路由变化');
      
      // 更新页面类型状态
      isConfirmationPage.value = route.path === '/confirm';
      isMyMeetingsPage.value = route.path === '/my-meetings';
      console.log('MeetingConfirmation路由监听 - 更新后isConfirmationPage:', isConfirmationPage.value);
      console.log('MeetingConfirmation路由监听 - 更新后isMyMeetingsPage:', isMyMeetingsPage.value);

      // 重置分页和会议数据
      meetings.value = [];
      page.value = 1;
      hasMore.value = true;
      console.log('MeetingConfirmation路由监听 - 重置分页和数据');

      // 如果路由query参数中包含会议ID，将其设置到搜索框中
      if (route.query.meetingId) {
        searchKeyword.value = route.query.meetingId;
        console.log('MeetingConfirmation路由监听 - 设置searchKeyword:', searchKeyword.value);
      }

      // 根据路由query中的status参数设置选中的标签页
      console.log('MeetingConfirmation路由监听 - status参数:', route.query.status);
      if (route.query.status) {
        // 直接使用传入的status作为index值（0:全部、1:待开展、2:邀约中、3:已结束）
        const index = parseInt(route.query.status, 10);
        console.log('MeetingConfirmation - 转换后的index:', index);
        activeTab.value = isNaN(index) ? 0 : index;
      } else {
        // 没有status参数时，重置为全部
        activeTab.value = 0;
      }
      console.log('MeetingConfirmation - 设置后的activeTab:', activeTab.value);

      // 重新获取会议列表
      fetchMeetings();
    }, { deep: true, immediate: true });
    

    return {
      isConfirmationPage,
      showMeetingDetail,
      selectedMeeting,
      meetings,
      loading,
      loadingMore,
      hasMore,
      activeTab,
      route,
      searchKeyword,
      handleMeetingClick,
      handleModifyMeeting,
      handleMeetingCancel,
      handleConfirmAttend,
      handleCannotAttend,
      handleTabChange,
      handleLoadMore
    };
  }
};
</script>

<style scoped>
.meeting-confirmation {
  font-family: 'PingFang SC', '微软雅黑', sans-serif;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 内容区域样式 - 占满剩余空间并预留底部导航栏 */
.body-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: calc(100vh - 96px);
  /* 减去头部46px和底部导航栏50px */
  overflow-y: auto;
}

/* 弹窗样式 */
.dialog-content {
  padding: 10px 0;
  max-height: 60vh;
  overflow-y: auto;
}
</style>